import React, { FunctionComponent } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import {
  composeValidators,
  digitsOnly,
  emailValidation,
  fileValidation,
  requiredValidation,
} from 'helpers/validations';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';

import { IDivorcesFormData, IDivorcesFormProps } from './types';
import './i18n';

const DivorcesForm: FunctionComponent<IDivorcesFormProps> = (props: IDivorcesFormProps) => {
  const { files, idCardTypes, initialValues, isEditing, onSubmit } = props;

  const tabs = [
    i18next.t<string>('DIVORCES_FORM:MARRIAGE'),
    i18next.t<string>('DIVORCES_FORM:HUSBAND'),
    i18next.t<string>('DIVORCES_FORM:WIFE'),
    i18next.t<string>('DIVORCES_FORM:COMMENTS'),
  ];

  return (
    <ViewContainer auth data-testid="divorce-form">
      <Title>{i18next.t<string>('DIVORCES_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IDivorcesFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          <MultiStepForm.Page key="divorce-page">
            <Typography variant="h6">
              {i18next.t<string>('DIVORCES_FORM:MARRIAGE_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('DIVORCES_FORM:WEDDING_TEMPLE')}
                name="weddingTemple"
                render={Input}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('DIVORCES_FORM:WEDDING_DATE')}
                name="weddingDate"
                render={Input}
                type="date"
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('DIVORCES_FORM:KETUBA_FILE')}
                name="ketubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DIVORCES_FORM:KETUBA_FILE'),
                    value: files?.ketubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <Field
                label={i18next.t<string>('DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="marriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.marriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <Field
              label={i18next.t<string>('DIVORCES_FORM:DIVORCE_CERTIFICATE_FILE')}
              name="divorceCertificateFile"
              previewFiles={[
                {
                  label: i18next.t<string>('DIVORCES_FORM:DIVORCE_CERTIFICATE_FILE'),
                  value: files?.divorceCertificateFile,
                },
              ]}
              render={Input}
              type="file"
              validate={fileValidation}
            />
          </MultiStepForm.Page>
          {['husband', 'wife'].map((value) => (
            <MultiStepForm.Page key={`${value}-page`}>
              <Typography variant="h6">
                {i18next.t<string>(`DIVORCES_FORM:${value.toUpperCase()}_INFORMATION`)}
              </Typography>
              <br />
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:LAST_NAME')}
                  name={`${value}.lastName`}
                  render={Input}
                  validate={requiredValidation}
                />
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:FIRST_NAME')}
                  name={`${value}.firstName`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:ADDRESS')}
                  name={`${value}.address`}
                  render={Input}
                  validate={requiredValidation}
                />
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:APARTMENT')}
                  name={`${value}.apartment`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:LOCALITY')}
                  name={`${value}.locality`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('DIVORCES_FORM:AREA_CODE')}
                    maxLength={4}
                    name={`${value}.areaCode`}
                    parse={(v) => digitsOnly(v)}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <Field
                    label={i18next.t<string>('DIVORCES_FORM:PHONE')}
                    maxLength={8}
                    name={`${value}.phone`}
                    parse={(v) => digitsOnly(v)}
                    render={Input}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
              </FormRowContainer>
              <Field
                label={i18next.t<string>('DIVORCES_FORM:EMAIL')}
                name={`${value}.email`}
                render={Input}
                type="email"
                validate={emailValidation}
              />
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:ID_CARD_TYPE')}
                  name={`${value}.idCardType`}
                  options={idCardTypes}
                  render={SelectInput}
                  validate={requiredValidation}
                />
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:ID_CARD')}
                  name={`${value}.idCard`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:ID_CARD_FILE')}
                  name={`${value}.idCardFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('DIVORCES_FORM:ID_CARD_FILE'),
                      value: files?.[`${value}IdCardFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:ID_CARD_BACK_FILE')}
                  name={`${value}.idCardBackFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('DIVORCES_FORM:ID_CARD_BACK_FILE'),
                      value: files?.[`${value}IdCardBackFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:KETUBA_FILE')}
                  name={`${value}.ketubaFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('DIVORCES_FORM:KETUBA_FILE'),
                      value: files?.[`${value}KetubaFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={fileValidation}
                />
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE')}
                  name={`${value}.marriageCertificateFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE'),
                      value: files?.[`${value}MarriageCertificateFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={fileValidation}
                />
              </FormRowContainer>
            </MultiStepForm.Page>
          ))}
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('DIVORCES_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default DivorcesForm;
